import React, { useState } from "react";
import { Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useSEO } from "@/lib/seo";
import { ArrowRight, CheckCircle2, RefreshCcw, ShieldCheck, Clock, FileText, Workflow } from "lucide-react";

const PLATFORMS = [
  {
    name: "QuickBooks", tag: "Online",
    body: "Push paid invoices and received payouts straight into QuickBooks Online so your books reconcile themselves.",
  },
  {
    name: "Xero", tag: "Accounting",
    body: "Keep contacts, invoices and payment status in sync with Xero — no double entry, no month-end scramble.",
  },
  {
    name: "FreshBooks", tag: "Invoicing",
    body: "Mirror your PaySignal ledger into FreshBooks so client billing and your tracker always agree.",
  },
  {
    name: "Wave", tag: "Free books",
    body: "Send income records to Wave automatically — ideal for solo workers keeping costs lean.",
  },
];

const BENEFITS = [
  { Icon: RefreshCcw, title: "Two-way sync", body: "Mark an invoice paid in PaySignal and watch it settle in your accounting tool — and vice versa. One source of truth, always current." },
  { Icon: Clock, title: "No more double entry", body: "Stop re-typing the same invoice into two systems. Log it once in PaySignal; your books stay in lockstep." },
  { Icon: FileText, title: "Tax season, handled", body: "When every paid invoice and payout already lives in your ledger, year-end becomes an export — not an archaeology dig." },
  { Icon: ShieldCheck, title: "Read-only by default", body: "Connections are scoped and revocable. You choose what syncs, and you can disconnect in one tap at any time." },
];

const STEPS = [
  { n: "01", title: "Connect once", body: "Authorize your accounting platform with a secure OAuth handshake. We never see or store your password." },
  { n: "02", title: "Map your fields", body: "Match PaySignal clients and categories to your chart of accounts. Sensible defaults mean most people skip this entirely." },
  { n: "03", title: "Let it run", body: "From then on, every status change flows automatically. Review a sync log any time — nothing happens behind your back." },
];

function WaitlistForm() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/newsletter/subscribe", { email, source: "integrations_waitlist" });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-[2rem] bg-espresso p-8 sm:p-14 relative overflow-hidden" data-testid="integrations-waitlist">
      <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full bg-olive/25 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-16 -left-12 w-56 h-56 rounded-full bg-lime/15 blur-3xl pointer-events-none" />
      <div className="relative max-w-xl">
        <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.22em] uppercase text-lime"><Workflow size={14} /> Early access</span>
        <h2 className="font-serif font-light text-3xl sm:text-4xl tracking-tight text-cream mt-4">Be first to sync your books.</h2>
        <p className="text-cream/70 mt-3">Accounting sync is in active development. Join the waitlist and we'll invite you the moment your platform goes live.</p>
        {done ? (
          <div className="mt-6 flex items-center gap-2 text-lime font-semibold" data-testid="integrations-waitlist-success">
            <CheckCircle2 size={20} /> You're on the list — we'll be in touch soon!
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-3">
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com" data-testid="integrations-waitlist-email"
              className="bg-cream/10 border-cream/20 text-cream placeholder:text-cream/50 sm:flex-1 rounded-full h-12 px-5" />
            <Button type="submit" disabled={busy} className="bg-lime text-espresso hover:bg-lime-dark rounded-full px-6 h-12" data-testid="integrations-waitlist-submit">
              {busy ? "Joining…" : "Request early access"}
            </Button>
          </form>
        )}
        <p className="text-xs text-cream/50 mt-4">No spam. We'll only email you about integration availability.</p>
      </div>
    </div>
  );
}

export default function Integrations() {
  useSEO({
    title: "Accounting Integrations — Sync PaySignal with QuickBooks, Xero & more",
    description: "Connect PaySignal to QuickBooks, Xero, FreshBooks and Wave. Two-way sync keeps invoices and payouts in lockstep with your books — no double entry. Join the early-access waitlist.",
    canonical: window.location.origin + "/integrations",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "PaySignal Accounting Integrations",
      description: "Sync your PaySignal invoices and payouts with QuickBooks, Xero, FreshBooks and Wave.",
    },
  });

  return (
    <div className="min-h-screen bg-cream text-espresso">
      <SiteHeader />

      {/* Hero */}
      <section className="max-w-3xl mx-auto px-6 pt-20 pb-12 text-center">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-[#8C6D58]">
          <span className="w-6 h-px bg-[#8C6D58]" /> Integrations · Coming soon
        </span>
        <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-6 text-espresso leading-[1.08]" data-testid="integrations-title">
          Your ledger, <em className="italic text-olive">in sync with your books.</em>
        </h1>
        <p className="text-lg text-espresso-soft mt-6 leading-relaxed">
          PaySignal is building a direct bridge to the accounting platforms you already use. Log a payment once and let it flow, automatically, into QuickBooks, Xero, FreshBooks or Wave.
        </p>
        <div className="flex flex-wrap justify-center gap-3 mt-9">
          <a href="#waitlist"><Button size="lg" className="rounded-full px-7 h-12" data-testid="integrations-hero-cta">Join the waitlist <ArrowRight size={18} className="ml-1.5" /></Button></a>
          <Link to="/how-it-works">
            <Button size="lg" variant="outline" className="rounded-full px-7 h-12 border-olive/40 text-olive hover:bg-olive-light hover:text-olive" data-testid="integrations-how">
              See how PaySignal works
            </Button>
          </Link>
        </div>
      </section>

      {/* Platforms */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <div className="grid sm:grid-cols-2 gap-5">
          {PLATFORMS.map((p) => (
            <div key={p.name} data-testid={`integration-${p.name.toLowerCase()}`}
              className="border border-warmborder rounded-2xl bg-white p-6 hover:border-olive/40 transition-colors">
              <div className="flex items-center justify-between">
                <span className="grid place-items-center w-12 h-12 rounded-2xl bg-olive/10 text-olive font-serif text-xl font-light">{p.name[0]}</span>
                <span className="text-xs uppercase tracking-wider font-semibold text-warmmuted">{p.tag}</span>
              </div>
              <h3 className="font-heading text-xl font-semibold text-espresso mt-5">{p.name}</h3>
              <p className="text-sm text-espresso-soft mt-2 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
        <p className="text-center text-sm text-warmmuted mt-6">Don't see yours? <Link to="/contact" className="underline hover:text-olive" data-testid="integrations-request-platform">Tell us which platform to build next.</Link></p>
      </section>

      {/* Benefits */}
      <section className="bg-olive-light border-y border-warmborder">
        <div className="max-w-5xl mx-auto px-6 py-20">
          <div className="text-center max-w-xl mx-auto">
            <span className="text-xs uppercase tracking-[0.22em] font-semibold text-olive">Why sync</span>
            <h2 className="font-serif font-normal text-3xl sm:text-4xl tracking-tight text-espresso mt-4 leading-tight">One entry. Two systems. Zero reconciliation.</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-6 mt-12">
            {BENEFITS.map(({ Icon, title, body }) => (
              <div key={title} className="flex gap-4" data-testid={`benefit-${title.toLowerCase().replace(/\s+/g, "-")}`}>
                <span className="grid place-items-center w-12 h-12 rounded-2xl bg-white text-olive shrink-0 border border-warmborder"><Icon size={22} /></span>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-espresso">{title}</h3>
                  <p className="text-sm text-espresso-soft mt-1.5 leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How sync works */}
      <section className="max-w-5xl mx-auto px-6 py-20">
        <h2 className="font-serif font-normal text-3xl sm:text-4xl tracking-tight text-espresso text-center">How the sync will work</h2>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {STEPS.map((s) => (
            <div key={s.n} className="border border-warmborder rounded-2xl bg-white p-7" data-testid={`sync-step-${s.n}`}>
              <span className="font-serif text-5xl font-light text-olive/30 leading-none">{s.n}</span>
              <h3 className="font-heading text-xl font-semibold text-espresso mt-4">{s.title}</h3>
              <p className="text-sm text-espresso-soft mt-2 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Waitlist */}
      <section id="waitlist" className="max-w-6xl mx-auto px-6 pb-24 scroll-mt-20">
        <WaitlistForm />
      </section>

      <SiteFooter />
    </div>
  );
}
